import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Play, Gamepad2, Music, Brain, Swords } from 'lucide-react';

const games = [
  {
    id: 'anunnaki',
    title: 'Anunnaki vs Humans',
    description: 'Epic RPG battle between ancient gods and humanity. Choose your faction and fight for dominance.',
    icon: Swords,
    color: '#D9540C',
    bgGradient: 'from-orange-900 via-red-900 to-black',
    link: '/game',
    status: 'Play Now'
  },
  {
    id: 'gnostic-quiz',
    title: 'The Contract of Ascension',
    description: 'Interactive video quiz on the Gnostic / Contracted Descent framework. 6 reflective questions.',
    icon: Brain,
    color: '#8B5CF6',
    bgGradient: 'from-purple-900 via-indigo-900 to-black',
    link: '/games/quiz',
    status: 'Play Now'
  },
  {
    id: 'music-gauntlet',
    title: 'Lines and Lyrics',
    description: 'The Music Gauntlet. Four actors, four movies — one word from each title spells the song. Name that song.',
    icon: Music,
    color: '#F59E0B',
    bgGradient: 'from-amber-800 via-orange-900 to-black',
    link: '/games/music-gauntlet',
    status: 'Play Now'
  }
];

export default function GamesPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/10 py-16 px-4">
      <Helmet>
        <title>Games | MGN1.6 | Grad P Fawkes</title>
        <meta name="description" content="Play games from the MGN1.6 universe - Anunnaki vs Humans RPG, The Contract of Ascension quiz, and The Music Gauntlet." />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Gamepad2 className="w-12 h-12 mx-auto mb-4 text-secondary" />
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Games
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Interactive experiences from the MGN1.6 universe
          </p>
        </motion.div>

        {/* Game Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game, i) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              {game.status === 'Coming Soon' ? (
                <div 
                  className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${game.bgGradient} p-6 h-full min-h-[280px] flex flex-col justify-between border border-white/10 opacity-70`}
                >
                  <div>
                    <game.icon className="w-10 h-10 mb-4" style={{ color: game.color }} />
                    <h2 className="text-2xl font-bold text-white mb-2">{game.title}</h2>
                    <p className="text-white/70 text-sm">{game.description}</p>
                  </div>
                  <div className="mt-4">
                    <span className="inline-block px-4 py-2 rounded-lg bg-white/10 text-white/60 text-sm font-semibold">
                      {game.status}
                    </span>
                  </div>
                </div>
              ) : (
                <Link 
                  to={game.link}
                  className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${game.bgGradient} p-6 h-full min-h-[280px] flex flex-col justify-between border border-white/10 hover:border-white/30 transition-all hover:scale-[1.02] hover:shadow-2xl group block`}
                  data-testid={`game-${game.id}`}
                >
                  <div>
                    <game.icon className="w-10 h-10 mb-4 transition-transform group-hover:scale-110" style={{ color: game.color }} />
                    <h2 className="text-2xl font-bold text-white mb-2">{game.title}</h2>
                    <p className="text-white/70 text-sm">{game.description}</p>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <span 
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all group-hover:gap-3"
                      style={{ backgroundColor: game.color, color: 'white' }}
                    >
                      <Play className="w-4 h-4" />
                      {game.status}
                    </span>
                  </div>
                  {/* Decorative glow */}
                  <div 
                    className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                    style={{ backgroundColor: game.color }}
                  />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
